<template>
  <div class="group-page">
    <confirm-dialog ref="confirmDialog" />

    <div class="table-wrap">
      <!-- DATA TABLE -->
      <v-data-table
        class="group-table-bulk group-item"
        :headers="headers"
        :items="sortedItems"
        :loading="loading"
        :page.sync="pageLocal"
        :items-per-page.sync="sizeLocal"
        :server-items-length="totalItems"
        hide-default-footer
        :item-class="rowClass"
        :mobile-breakpoint="0"
        @click:row="goToGroup"
      >
        <!-- TOP BAR / TOOLBAR -->
        <template #top>
          <div class="elevated-toolbar px-4 py-3 d-flex align-center justify-space-between">
            <div class="d-flex align-center flex-wrap">
              <v-text-field
                v-model="searchQuery"
                append-icon="mdi-magnify"
                :placeholder="$t('Izlash...')"
                dense
                clearable
                outlined
                class="search-input mr-3 mb-2 mb-sm-0"
                hide-details
                @input="onSearch"
              />

              <!-- ACTIVE FILTER CHIPS (inline, modern) -->
              <div class="d-flex align-center flex-wrap" v-if="activeFilterCount">
                <v-chip
                  v-for="c in activeFilterChips"
                  :key="c.key + '_' + c.value"
                  class="ma-1 soft-chip"
                  small
                  close
                  @click:close="removeFilterChip(c)"
                >
                  <v-icon left small class="mr-1">{{ c.icon }}</v-icon>
                  {{ c.label }}
                </v-chip>
              </div>
            </div>

            <div class="d-flex align-center">
              <!-- FILTER BUTTON WITH BADGE/COUNT -->
              <v-badge v-if="activeFilterCount" :content="activeFilterCount" color="primary" overlap>
                <v-btn class="filter-btn mx-2" depressed @click="openFilter">
                  <v-icon left>mdi-tune</v-icon>
                  <span class="hidden-sm-and-down">{{ $t('Filters') }}</span>
                </v-btn>
              </v-badge>
              <v-btn v-else class="filter-btn mx-2" depressed @click="openFilter">
                <v-icon left>mdi-filter-variant</v-icon>
                <span class="hidden-sm-and-down">{{ $t('Filters') }}</span>
              </v-btn>

              <!-- EXCEL EXPORT -->
              <v-btn
                class="ml-2 modern-button modern-export"
                :loading="exportingExcel"
                :disabled="!filter.year || excelExportDisabled"
                @click="exportPaymentExcel"
              >
                <img src="/icons/excel-icon.svg" alt="Excel" class="export-icon">
                EXPORT
              </v-btn>

              <!-- ADD BUTTON -->
              <v-btn v-if="canCreate" class="add-btn mx-2 font-made" depressed @click="openDialog">
                <v-icon left class="plus-icon">mdi-plus</v-icon>
                <span>{{ $t('ADD GROUP') }}</span>
              </v-btn>

              <v-chip class="ml-3 badge-count" color="primary" small>
                {{ totalItems || 0 }}
              </v-chip>
            </div>
          </div>

          <!-- INLINE FILTER BLOCK (glassmorphism) -->
          <v-expand-transition>
            <v-sheet
              v-if="dialogFilter"
              class="filter-block mb-3 pa-4"
              elevation="1"
              outlined
            >
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <v-icon class="mr-2">mdi-tune-vertical</v-icon>
                  <strong>{{ $t('Filter Groups') }}</strong>
                </div>
                <div class="d-flex align-center">
                  <v-btn small class="reset-btn mr-1" text @click="resetFilters">
                    <v-icon left small>mdi-backspace-reverse-outline</v-icon>
                    {{ $t('Reset') }}
                  </v-btn>
                  <v-btn icon @click="closeFilter">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <label class="field-label">{{ $t('Faculty') }}</label>
                  <v-autocomplete
                    v-model="filter.educationFacultyIds"
                    :items="educationFaculties"
                    item-value="id"
                    :item-text="t => t?.names?.uz ?? t?.name"
                    multiple chips deletable-chips clearable outlined dense
                    :loading="loadingFaculties"
                    :menu-props="{ bottom: true, offsetY: true, maxHeight: 260 }"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <label class="field-label">{{ $t('Education Type') }}</label>
                  <v-autocomplete
                    v-model="filter.educationTypeIds"
                    :items="educationTypes"
                    item-value="id"
                    :item-text="t => t?.names?.uz ?? t?.name"
                    multiple chips deletable-chips clearable outlined dense
                    :loading="loadingTypes"
                    :menu-props="{ bottom: true, offsetY: true, maxHeight: 260 }"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <label class="field-label">{{ $t('Language') }}</label>
                  <v-autocomplete
                    v-model="filter.educationLanguageIds"
                    :items="educationLanguages"
                    item-value="id"
                    :item-text="t => t?.names?.uz ?? t?.name"
                    multiple chips deletable-chips clearable outlined dense
                    :loading="loadingLanguages"
                    :menu-props="{ bottom: true, offsetY: true, maxHeight: 260 }"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <label class="field-label">{{ $t('Year') }}</label>
                  <v-select
                    v-model="filter.year"
                    :items="yearOptions"
                    :placeholder="$t('All Years')"
                    clearable outlined dense hide-details
                    :menu-props="{ bottom: true, offsetY: true, maxHeight: 260 }"
                  />
                </v-col>

              </v-row>

              <div class="d-flex align-center justify-end mt-2">
                <button class="grp-btn-cancel mr-2" @click="closeFilter">{{ $t('Cancel') }}</button>
                <button class="grp-btn-search" @click="applyFilters">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/></svg>
                  {{ $t('Apply search') }}
                </button>
              </div>
            </v-sheet>
          </v-expand-transition>
        </template>

        <!-- COLUMNS -->
        <template #item.rowIndex="{ item, index }">
          <div class="row-index-cell">
            <span v-if="getPctRank(item) <= 5" class="pct-rank-badge">
              {{ getPctRank(item) }}
            </span>
            <span v-else class="row-num">{{ (pageLocal - 1) * sizeLocal + index + 1 }}</span>
          </div>
        </template>
        <template #item.code="{ item }">{{ item.code }}</template>
        <template #item.directionName="{ item }">{{ item.directionName }}</template>
        <template #item.form="{ item }">{{ item.form }}</template>
        <template #item.language="{ item }">{{ item.language }}</template>
        <template #item.totalStudents="{ item }">{{ item.totalStudents }}</template>

        <template #item.contractSum="{ item }">
          <span class="text-no-wrap">{{ formatMoney(item.totalContractSum) }}</span>
        </template>
        <template #item.paidSum="{ item }">
          <span class="text-no-wrap paid-text">{{ formatMoney(item.totalPaidSum) }}</span>
        </template>
        <template #header.paidPercent="{ header }">
          <span class="d-inline-flex align-center" style="cursor:pointer;user-select:none" @click="togglePctSort">
            {{ header.text }}
            <v-icon small class="ml-1">{{ pctSortDir === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
          </span>
        </template>

        <template #item.paidPercent="{ item }">
          <div class="d-flex align-center" style="min-width:100px">
            <div class="pct-bar mr-2">
              <div
                class="pct-bar-fill"
                :class="getPctRank(item) <= 5 ? 'pct-bar-gold' : pctColor(item.paidPercent || 0)"
                :style="{width: Math.min(item.paidPercent || 0, 100)+'%'}"
              />
            </div>
            <span
              class="caption font-weight-bold"
              :class="getPctRank(item) <= 5 ? 'pct-text-gold' : pctTextColor(item.paidPercent || 0)"
            >{{ item.paidPercent || 0 }}%</span>
          </div>
        </template>

        <template #item.teacherName="{ item }">
          <div v-if="item.teacherName" class="d-flex align-center">
            <v-icon v-if="isTeacherFemale(item.teacherName)" size="18" color="#d81b60" class="mr-1">mdi-human-female</v-icon>
            <v-icon v-else size="18" color="#1565c0" class="mr-1">mdi-human-male</v-icon>
            <span>{{ item.teacherName }}</span>
          </div>
        </template>

        <template #item.actions="{ item }">
          <div class="tc-actions" @click.stop>
            <button v-if="canUpdate" class="tc-act" :title="$t('Edit')" @click="editItem(item)">
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
            </button>
            <button v-if="canDelete" class="tc-act tc-act--delete" :title="$t('Delete')" @click="deleteItem(item)">
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            </button>
          </div>
        </template>

        <!-- PROGRESS / SKELETON -->
        <template #progress>
          <table-skeleton :cols="headers.length" />
        </template>

        <!-- EMPTY STATE -->
        <template #body.append>
          <tr v-if="!loading && items.length === 0">
            <td :colspan="headers.length">
              <empty-state
                :title="$t('No results found')"
                :subtitle="$t('Try changing filters or clearing your search')"
              />
            </td>
          </tr>
        </template>

        <!-- FOOTER -->
        <template #footer>
          <div class="px-4 pb-4 d-flex align-center justify-space-between flex-wrap">
            <div class="caption grey--text text--darken-1 my-2">
              {{ displayFrom }}–{{ displayTo }} / {{ totalItems }}
            </div>
            <div class="d-flex align-center my-2">
              <v-pagination
                v-model="pageLocal"
                :length="pageCount"
                total-visible="7"
                prev-icon="mdi-chevron-left"
                next-icon="mdi-chevron-right"
                circle large
              />
            </div>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- CREATE/EDIT DIALOG -->
    <v-dialog v-model="dialog" max-width="520px" content-class="grp-dialog-wrap">
      <div class="grp-modal">
        <div class="grp-modal-header">
          <h3>{{ editing ? $t('Edit Group') : $t('Add New Group') }}</h3>
          <button class="grp-modal-close" @click="dialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="grp-modal-body">
          <div class="grp-field">
            <label class="grp-label">{{ $t('Name') }}</label>
            <v-text-field
              v-model="form.code"
              :placeholder="$t('Enter group name')"
              required outlined dense hide-details
            />
          </div>

          <div class="grp-field">
            <label class="grp-label">{{ $t('Education Type') }}</label>
            <v-autocomplete
              v-model="form.educationType"
              :items="educationTypes"
              :item-text="t => t?.names?.uz ?? t?.name"
              return-object
              :placeholder="$t('Select education type')"
              required
              :loading="loadingTypes"
              clearable outlined dense hide-details
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
            />
          </div>

          <div class="grp-field">
            <label class="grp-label">{{ $t('Education Language') }}</label>
            <v-autocomplete
              v-model="form.educationLanguage"
              :items="educationLanguages"
              :item-text="t => t?.names?.uz ?? t?.name"
              return-object
              :placeholder="$t('Select language')"
              required
              :loading="loadingLanguages"
              clearable outlined dense hide-details
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
            />
          </div>

          <div class="grp-field">
            <label class="grp-label">{{ $t('Education Faculty') }}</label>
            <v-autocomplete
              v-model="form.educationFaculty"
              :items="educationFaculties"
              :item-text="t => t?.names?.uz ?? t?.name"
              return-object
              :placeholder="$t('Select faculty')"
              required
              :loading="loadingFaculties"
              clearable outlined dense hide-details
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
            />
          </div>

          <div class="grp-field" v-if="editing">
            <label class="grp-label">{{ $t('Teacher') }}</label>
            <v-autocomplete
              v-model="form.teacherId"
              :items="teachers"
              item-text="name"
              item-value="id"
              :placeholder="$t('Select teacher')"
              :loading="loadingTeachers"
              clearable outlined dense hide-details
              :menu-props="{ bottom: true, offsetY: true, maxHeight: 200 }"
            />
          </div>
        </div>

        <div class="grp-modal-footer">
          <button class="grp-btn-cancel" @click="dialog = false">
            {{ $t('Cancel') }}
          </button>
          <button class="grp-btn-save font-made" @click="saveGroup">
            {{ editing ? $t('Update') : $t('Add') }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import { generateYearOptions, getStoredYear, setStoredYear } from '@/service/year-options'
var fileDownload = require('js-file-download')
import ConfirmDialog from '@/components/group/ConfirmDialog.vue'
import EmptyState from '@/components/EmptyState.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'

export default {
  name: 'GroupTableBulk',
  components: { ConfirmDialog, EmptyState, TableSkeleton },
  data() {
    return {
      canCreate: false,
      canUpdate: false,
      canDelete: false,
      dialog: false,
      dialogFilter: false,
      editing: false,
      searchDebounce: null,

      form: {
        id: null,
        code: '',
        educationType: null,
        educationLanguage: null,
        educationFaculty: null,
        teacherId: null,
        countOfStudentsInGroup: 0,
        countOfMaleStudentsInGroup: 0,
        countOfFemaleStudentsInGroup: 0,
      },

      educationTypes: [],
      educationLanguages: [],
      educationFaculties: [],
      teachers: [],
      loadingTypes: false,
      loadingLanguages: false,
      loadingFaculties: false,
      loadingTeachers: false,

      items: [],
      loading: false,
      totalItems: 0,
      searchQuery: '',
      pageLocal: 1,
      sizeLocal: 100,

      // filters
      filter: {
        educationFacultyIds: [],
        educationTypeIds: [],
        educationLanguageIds: [],
        year: getStoredYear() || String(new Date().getFullYear() - 1),
        active: null,
      },

      exportingExcel: false,
      pctSortDir: 'desc',

      headers: [
        { text: '#', value: 'rowIndex', sortable: false, width: '50px' },
        { text: this.$t('Name'), value: 'code' },
        { text: this.$t('Education direction'), value: 'directionName' },
        { text: this.$t('Education Type'), value: 'form' },
        { text: this.$t('Language'), value: 'language' },
        { text: this.$t('Students'), value: 'totalStudents' },
        { text: this.$t('Contract sum'), value: 'contractSum', sortable: false },
        { text: this.$t('Paid'), value: 'paidSum', sortable: false },
        { text: this.$t('Payment') + ' %', value: 'paidPercent', sortable: false, class: 'pct-header' },
        { text: this.$t('Teacher'), value: 'teacherName', sortable: false, width: '220px' },
        { text: '', value: 'actions', sortable: false },
      ],

      yearOptions: generateYearOptions('short'),

      statusItems: [
        { text: this.$t ? this.$t('All') : 'All', value: null },
        { text: this.$t ? this.$t('Active') : 'Active', value: true },
        { text: this.$t ? this.$t('Inactive') : 'Inactive', value: false },
      ],
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.totalItems / this.sizeLocal) || 1
    },
    filterActive() {
      const f = this.filter
      return (
        (f.educationFacultyIds && f.educationFacultyIds.length) ||
        (f.educationTypeIds && f.educationTypeIds.length) ||
        (f.educationLanguageIds && f.educationLanguageIds.length) ||
        !!f.year ||
        f.active === true || f.active === false
      )
    },
    activeFilterCount() {
      let c = 0
      const f = this.filter
      c += (f.educationFacultyIds || []).length
      c += (f.educationTypeIds || []).length
      c += (f.educationLanguageIds || []).length
      if (f.year) c += 1
      if (f.active === true || f.active === false) c += 1
      return c
    },
    activeFilterChips() {
      const chips = []
      const add = (key, value, label, icon) => chips.push({ key, value, label, icon })

      ;(this.filter.educationFacultyIds || []).forEach((id) => {
        add('educationFacultyIds', id, this.getLabel(this.educationFaculties, id, 'faculty'), 'mdi-town-hall')
      })
      ;(this.filter.educationTypeIds || []).forEach((id) => {
        add('educationTypeIds', id, this.getLabel(this.educationTypes, id, 'type'), 'mdi-school')
      })
      ;(this.filter.educationLanguageIds || []).forEach((id) => {
        add('educationLanguageIds', id, this.getLabel(this.educationLanguages, id, 'language'), 'mdi-translate')
      })
      if (this.filter.year) {
        const yo = (this.yearOptions || []).find(o => o.value === this.filter.year)
        add('year', null, `${this.$t('Year')}: ${yo ? yo.text : this.filter.year}`, 'mdi-calendar')
      }
      if (this.filter.active === true || this.filter.active === false) {
        add('active', null, `${this.$t('Status')}: ${this.filter.active ? this.$t('Active') : this.$t('Inactive')}`, this.filter.active ? 'mdi-check-decagram' : 'mdi-close-octagon')
      }
      return chips
    },
    sortedItems() {
      return [...this.items].sort((a, b) => {
        const ap = a.paidPercent || 0
        const bp = b.paidPercent || 0
        return this.pctSortDir === 'asc' ? ap - bp : bp - ap
      })
    },
    displayFrom() {
      if (!this.totalItems) return 0
      return (this.pageLocal - 1) * this.sizeLocal + 1
    },
    displayTo() {
      return Math.min(this.pageLocal * this.sizeLocal, this.totalItems)
    },
  },
  methods: {
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    // Table navigation
    goToGroup(item) {
      if (!this.hasGroupRow(item)) return
      this.$router.push({ path: `/group-show/${item.id}` })
    },

    // With a year chosen, the rows are built from the codes written on that year's contracts. A code
    // that no group carries comes back without an id, and opening, editing or deleting it used to call
    // /api/groups/null ("null is not of type class java.lang.Long"). Say what is actually missing.
    hasGroupRow(item) {
      if (item && item.id != null) return true
      this.$store.dispatch('SET_SNACKBAR', {
        text: this.$t('No group with this name exists yet. Add it with ADD GROUP first.'),
        color: 'error',
      })
      return false
    },

    // Inline filter open/close
    openFilter() {
      this.dialogFilter = !this.dialogFilter
    },
    closeFilter() {
      this.dialogFilter = false
    },

    // Helpers
    getLabel(list, id) {
      const it = (list || []).find((x) => String(x.id) === String(id))
      return it?.names?.uz || it?.name || id
    },
    removeFilterChip(chip) {
      const k = chip.key
      if (k === 'year') this.filter.year = null
      else if (k === 'active') this.filter.active = null
      else if (Array.isArray(this.filter[k])) {
        this.filter[k] = this.filter[k].filter((v) => String(v) !== String(chip.value))
      }
      this.applyFilters()
    },

    // Data fetching
    async fetchDropdowns() {
      this.loadingTypes = this.loadingLanguages = this.loadingFaculties = true
      try {
        const [tRes, lRes, fRes] = await Promise.all([
          axios.get('/admin/education-type', { params: { page: 0, size: 100 } }),
          axios.get('/admin/education-language', { params: { page: 0, size: 100 } }),
          axios.get('/admin/faculty', { params: { page: 0, size: 100 } }),
        ])
        this.educationTypes = tRes.data.content || []
        this.educationLanguages = lRes.data.content || []
        this.educationFaculties = fRes.data.content || []
      } finally {
        this.loadingTypes = this.loadingLanguages = this.loadingFaculties = false
      }
    },
    async fetchTeachers() {
      if (this.teachers.length) return
      this.loadingTeachers = true
      try {
        const { data } = await axios.get('/admin/teacher', {
          params: { page: 0, size: 1000, name: '', sortField: 'name', sortOrder: 'asc' }
        })
        this.teachers = data.content || []
      } catch (e) {
        console.error('Failed to fetch teachers:', e)
      } finally {
        this.loadingTeachers = false
      }
    },
    async fetchData() {
      this.loading = true
      try {
        if (this.filterActive) {
          const res = await axios.get('/api/groups/filter', {
            params: {
              page: this.pageLocal - 1,
              size: this.sizeLocal,
              sort: 'id,desc',
              code: this.searchQuery || undefined,
              educationFacultyIds: this.filter.educationFacultyIds,
              educationTypeIds: this.filter.educationTypeIds,
              educationLanguageIds: this.filter.educationLanguageIds,
              year: this.filter.year || undefined,
              active: this.filter.active,
            },
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
          })

          this.items = (res.data.content || []).map((g) => this.mapGroup(g))
          this.totalItems = res.data.totalElements || 0
        } else {
          const res = await axios.get('/api/groups', {
            params: {
              page: this.pageLocal - 1,
              size: this.sizeLocal,
              sort: 'id,desc',
              search: this.searchQuery || undefined,
              year: this.filter.year || undefined,
            },
          })
          this.items = (res.data.content || []).map((g) => this.mapGroup(g))
          this.totalItems = res.data.totalElements || 0
        }
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.pageLocal = 1
        this.fetchData()
      }, 300)
    },
    applyFilters() {
      this.pageLocal = 1
      this.fetchData()
      this.closeFilter()
    },
    resetFilters() {
      this.filter = {
        educationFacultyIds: [],
        educationTypeIds: [],
        educationLanguageIds: [],
        year: this.filter.year, // preserve year selection
        active: null,
      }
      this.applyFilters()
    },
    mapGroup(g) {
      return {
        id: g.id,
        code: g.code,
        directionName: g.educationFaculty?.name,
        form: g.educationType?.name,
        language: g.educationLanguage?.name,
        totalStudents: g.countOfStudentsInGroup,
        teacherName: g.teacherName || '',
        totalContractSum: g.totalContractSum || 0,
        totalPaidSum: g.totalPaidSum || 0,
        totalDebt: g.totalDebt || 0,
        paidPercent: g.paidPercent || 0,
      }
    },
    async exportPaymentExcel() {
      const year = this.filter.year
      if (!year || this.exportingExcel) return
      this.exportingExcel = true
      try {
        const res = await axios.get('/api/groups/payment/export', {
          params: {
            year,
            search: this.searchQuery || undefined,
            sortField: 'paidPercent',
            sortOrder: this.pctSortDir || 'desc',
          },
          responseType: 'arraybuffer',
        })
        const date = new Date().toISOString().substr(0, 10)
        fileDownload(res.data, `guruhlar_tolov_${year}_${date}.xlsx`)
      } catch (e) {
        console.error('Failed to export:', e)
      } finally {
        this.exportingExcel = false
      }
    },
    formatMoney(val) {
      if (!val) return '0'
      return Number(val).toLocaleString('uz-UZ')
    },
    isTeacherFemale(name) {
      if (!name) return false
      const surname = name.trim().split(/\s+/)[0].toLowerCase()
      return surname.endsWith('ova') || surname.endsWith('eva') || surname.endsWith('va')
    },
    togglePctSort() {
      this.pctSortDir = this.pctSortDir === 'desc' ? 'asc' : 'desc'
    },
    getPctRank(item) {
      if (this.searchQuery) return 999
      const sorted = [...this.items].sort((a, b) => (b.paidPercent || 0) - (a.paidPercent || 0))
      return sorted.findIndex(i => i.id === item.id) + 1
    },
    rowClass(item) {
      if (item.code === '000OOO') return 'danger-row'
      return this.getPctRank(item) <= 5 ? 'gold-row' : ''
    },
    pctColor(pct) {
      if (pct >= 80) return 'pct-green'
      if (pct >= 50) return 'pct-yellow'
      return 'pct-red'
    },
    pctTextColor(pct) {
      if (pct >= 80) return 'green--text text--darken-2'
      if (pct >= 50) return 'orange--text text--darken-2'
      return 'red--text text--darken-1'
    },
    openDialog() {
      this.editing = false
      this.form = {
        id: null,
        code: '',
        educationType: null,
        educationLanguage: null,
        educationFaculty: null,
        teacherId: null,
        countOfStudentsInGroup: 0,
        countOfMaleStudentsInGroup: 0,
        countOfFemaleStudentsInGroup: 0,
      }
      this.dialog = true
    },
    async saveGroup() {
      if (!this.form.code || !this.form.educationType || !this.form.educationLanguage || !this.form.educationFaculty) {
        this.$toast && this.$toast.error('Please fill in all required fields')
        return
      }
      const payload = {
        code: this.form.code,
        educationType: { id: this.form.educationType.id },
        educationLanguage: { id: this.form.educationLanguage.id },
        educationFaculty: { id: this.form.educationFaculty.id },
        countOfStudentsInGroup: this.form.countOfStudentsInGroup,
        countOfMaleStudentsInGroup: this.form.countOfMaleStudentsInGroup,
        countOfFemaleStudentsInGroup: this.form.countOfFemaleStudentsInGroup,
      }
      try {
        if (this.editing && this.form.id) {
          await axios.put(`/api/groups/${this.form.id}`, payload)
          await axios.put(`/api/groups/${this.form.id}/teacher`, null, {
            params: { teacherId: this.form.teacherId || '' }
          })
        } else {
          await axios.post('/api/groups', payload)
        }
        this.dialog = false
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$toast && this.$toast.error('Failed to save group')
      }
    },
    findById(list, id) {
      if (id == null) return null
      return list.find((item) => String(item.id) === String(id)) || null
    },
    findByName(list, name) {
      if (!name) return null
      const n = (v) => (v || '').toString().trim().toLowerCase()
      return (
        (list || []).find(
          (x) =>
            n(x?.name) === n(name) ||
            n(x?.names?.uz) === n(name) ||
            n(x?.names?.ru) === n(name) ||
            n(x?.names?.en) === n(name)
        ) || null
      )
    },
    async editItem(item) {
      if (!this.hasGroupRow(item)) return
      this.editing = true

      if (!this.educationTypes.length || !this.educationLanguages.length || !this.educationFaculties.length) {
        await this.fetchDropdowns()
      }
      this.fetchTeachers()

      let full = item
      if (!item.educationType || !item.educationLanguage || !item.educationFaculty) {
        const { data } = await axios.get(`/api/groups/${item.id}`)
        full = data
      }

      this.form.id = full.id
      this.form.code = full.code

      const etId = full.educationType?.id
      const elId = full.educationLanguage?.id
      const efId = full.educationFaculty?.id

      this.form.educationType = etId
        ? this.findById(this.educationTypes, etId)
        : this.findByName(this.educationTypes, full.educationType?.name || full.educationType?.names?.uz)

      this.form.educationLanguage = elId
        ? this.findById(this.educationLanguages, elId)
        : this.findByName(this.educationLanguages, full.educationLanguage?.name || full.educationLanguage?.names?.uz)

      this.form.educationFaculty = efId
        ? this.findById(this.educationFaculties, efId)
        : this.findByName(this.educationFaculties, full.educationFaculty?.name || full.educationFaculty?.names?.uz)

      this.form.teacherId = full.teacherId || null
      this.form.countOfStudentsInGroup = full.countOfStudentsInGroup ?? full.totalStudents ?? 0
      this.form.countOfMaleStudentsInGroup = full.countOfMaleStudentsInGroup ?? 0
      this.form.countOfFemaleStudentsInGroup = full.countOfFemaleStudentsInGroup ?? 0

      this.dialog = true
    },
    async deleteItem(item) {
      if (!this.hasGroupRow(item)) return
      const confirmed = await this.$refs.confirmDialog.open()
      if (!confirmed) return

      axios
        .delete(`/api/groups/${item.id}`)
        .then(() => this.fetchData())
        .catch((e) => {
          console.error(e)
          this.$toast && this.$toast.error('Failed to delete group')
        })
    },
  },
  watch: {
    pageLocal() {
      this.fetchData()
    },
    sizeLocal() {
      this.pageLocal = 1
      this.fetchData()
    },
    'filter.year'(val) {
      setStoredYear(val)
    },
  },
  async mounted() {
    this.canCreate = await this.hasRole("GURUHLAR", "CREATE");
    this.canUpdate = await this.hasRole("GURUHLAR", "UPDATE");
    this.canDelete = await this.hasRole("GURUHLAR", "DELETE");
    this.fetchData()
    this.fetchDropdowns()
  },
}
</script>

<style lang="scss" scoped>
.group-page {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}
.group-table-bulk { width: 100%; }
.group-item        { cursor: pointer; }
.table-wrap        { position: relative; }
::v-deep .group-table-bulk td:first-child,
::v-deep .group-table-bulk th:first-child { padding-left: 16px !important; }
::v-deep .group-table-bulk td:last-child,
::v-deep .group-table-bulk th:last-child { padding-right: 8px !important; }
.row-index-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
}
.row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}

/* Navbar toolbar */
.elevated-toolbar {
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.08);
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 5;
}
.table-wrap {
  padding: 0 16px 16px;
}
.search-input { min-width: 260px; max-width: none; }

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}

/* Inline filter block — glassmorphism */
.filter-block {
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0,0,0,.06);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,.08);
}

/* Buttons */
.filter-btn {
  position: relative;
  border-radius: 12px !important;
  height: 40px;
  padding: 0 16px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: .2px;
  background: linear-gradient(180deg, #fafafa, #f3f3f3) !important;
  border: 1px solid rgba(0,0,0,.06) !important;
  box-shadow: 0 2px 10px rgba(0,0,0,.08);
  transition: transform .12s ease, box-shadow .2s ease, background .2s ease;
}
.filter-btn .v-icon { opacity: .9; }
.filter-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(0,0,0,.12); }
.filter-btn:active { transform: translateY(0); box-shadow: 0 3px 10px rgba(0,0,0,.10); }
.badge-dot {
  position: absolute; top: 8px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: #1976d2;
}

.add-btn {
  height: 41px;
  border-radius: 12px;
  padding: 15px 16px !important;
  text-transform: none;
  font-weight: 700;
  letter-spacing: .2px;
  background: #ffd451 !important;
  color: #111318 !important;
  border: none !important;
  box-shadow: none;
  transition: background-color .15s ease;
}
.add-btn:hover { background: #f0c030 !important; }
.add-btn:active { background: #e0b020 !important; }
.add-btn .v-icon { font-size: 18px; margin-right: 6px; color: #111318 !important; }
.add-btn span { color: #111318 !important; }

/* Field labels */
.field-label {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .5px;
  text-transform: uppercase;
  margin-bottom: 6px;
  opacity: .75;
}
.reset-btn { text-transform: none; font-weight: 600; }

.grp-btn-search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 22px;
  border-radius: 10px;
  border: none;
  background: #0a2749;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all .15s;
}
.grp-btn-search:hover {
  background: #0d3460;
}
.grp-btn-search svg {
  flex-shrink: 0;
}

/* Chips */
.soft-chip { background: rgba(0,0,0,.04) !important; border: 1px solid rgba(0,0,0,.06); }
.clear-all { background: rgba(25,118,210,.07) !important; }

/* Inputs — modern flat style */
::v-deep .v-input--dense .v-input__slot { border-radius: 10px !important; min-height: 38px !important; }
::v-deep .v-input__slot { transition: border-color .15s ease !important; }
::v-deep .v-input--outlined fieldset { border-width: 1px !important; border-color: #e0e0e0 !important; }
::v-deep .v-input--outlined.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
::v-deep .v-input--outlined legend { display: none !important; }
::v-deep .v-label { font-size: 13px !important; color: #9e9e9e !important; }
::v-deep .v-label--active { transform: none !important; display: none !important; }
::v-deep .v-input__icon .v-icon { font-size: 20px !important; color: #9e9e9e !important; transition: color .15s ease !important; }
::v-deep .v-input--is-focused .v-input__icon .v-icon { color: #00274c !important; }
::v-deep .v-input__append-inner { margin-top: 0 !important; align-self: center !important; }
::v-deep .v-select__selection { font-size: 14px !important; font-weight: 500 !important; }
::v-deep .v-text-field input { font-size: 14px !important; font-weight: 500 !important; }

/* Footer */
.per-page-select { min-width: 94px; }

/* Responsive tweaks */
@media (max-width: 960px) {
  .elevated-toolbar {
    flex-wrap: wrap !important;
    gap: 8px;
    padding: 12px !important;
  }
  .elevated-toolbar > div:first-child {
    flex: 1 1 100% !important;
  }
  .elevated-toolbar > div:last-child {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .search-input { min-width: 100% !important; margin-right: 0 !important; margin-bottom: 8px !important; }
  .filter-btn span { display: none; }
  .filter-btn { min-width: 40px !important; padding: 0 10px !important; }
  .filter-btn .v-icon { margin: 0 !important; }
  .modern-export { min-width: auto !important; padding: 8px 12px !important; font-size: 12px !important; }
  .add-btn span { display: none; }
  .add-btn { min-width: 40px !important; padding: 0 10px !important; }
  .add-btn .plus-icon { margin: 0 !important; }
  .badge-count { margin-left: 0 !important; }
}

@media (max-width: 600px) {
  .table-wrap { padding: 0; }
  .group-page { padding: 0 !important; }
  ::v-deep .group-table-bulk { overflow-x: auto; }
  ::v-deep .group-table-bulk table { min-width: 800px; }
  ::v-deep .group-table-bulk td { padding: 10px 12px !important; font-size: 13px !important; white-space: nowrap; }
  ::v-deep .group-table-bulk th { padding: 10px 12px !important; font-size: 11px !important; white-space: nowrap; }
}

/* Row action buttons */
.tc-actions { display: flex; gap: 6px; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

/* ═══════════════════════════════════════
   GROUP MODAL
   ═══════════════════════════════════════ */
::v-deep .grp-dialog-wrap {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}
.grp-modal {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,.12);
}
.grp-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
}
.grp-modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #111318;
}
.grp-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: none;
  cursor: pointer;
  color: #6b7280;
  transition: all .15s;
}
.grp-modal-close:hover {
  border-color: #d1d5db;
  background: #f9fafb;
  color: #111318;
}
.grp-modal-body {
  padding: 8px 24px 16px;
}
.grp-field {
  margin-bottom: 16px;
}
.grp-field:last-child {
  margin-bottom: 0;
}
.grp-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: #6b7280;
  margin-bottom: 6px;
}
.grp-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 24px 20px;
}
.grp-btn-cancel {
  padding: 8px 20px;
  border-radius: 10px;
  border: 1px dashed #d1d5db;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all .15s;
}
.grp-btn-cancel:hover {
  border-color: #9ca3af;
  color: #111318;
}
.grp-btn-save {
  padding: 8px 24px;
  border-radius: 10px;
  border: none;
  background: #0a2749;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all .15s;
}
.grp-btn-save:hover {
  background: #0d3460;
}

/* Excel export button */
.modern-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #217346 !important;
  color: #fff !important;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
  min-width: 130px !important;
}
.modern-export:hover {
  background: #1a5c38 !important;
}
.modern-export:active {
  transform: scale(.97);
}
.export-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  margin-right: 10px;
}

/* Paid text */
.paid-text { color: #2e7d32; }

/* Payment percent bar */
.pct-bar {
  flex: 1;
  height: 6px;
  background: rgba(0,0,0,.06);
  border-radius: 3px;
  overflow: hidden;
}
.pct-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width .4s ease;
}
.pct-green { background: #2e7d32; }
.pct-yellow { background: #f9a825; }
.pct-red { background: #e53935; }

/* ═══════════════════════════════════════
   DARK THEME OVERRIDES
   ═══════════════════════════════════════ */

/* Toolbar */
.theme--dark .elevated-toolbar {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}

/* Filter block */
.theme--dark .filter-block {
  background: rgba(26,27,35,.92) !important;
  border-color: #2a2c3a !important;
  box-shadow: 0 12px 32px rgba(0,0,0,.3) !important;
}

/* Filter button */
.theme--dark .filter-btn {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
  box-shadow: 0 2px 10px rgba(0,0,0,.2) !important;
}
.theme--dark .filter-btn:hover {
  background: #22232e !important;
  border-color: #33354a !important;
  box-shadow: 0 8px 20px rgba(0,0,0,.3) !important;
}

/* Add button — dark theme */
.theme--dark .add-btn {
  background: #ffd451 !important;
  color: #111318 !important;
  box-shadow: none !important;
}

/* Search button dark */
.theme--dark .grp-btn-search {
  background: #ffd451;
  color: #111318;
}
.theme--dark .grp-btn-search:hover {
  background: #ffdc6e;
}

/* Frosted glass modal card */
.theme--dark ::v-deep .v-dialog .v-card {
  background: rgba(26, 27, 35, 0.85) !important;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid #2a2c3a;
}

/* Chips */
.theme--dark .soft-chip {
  background: #22232e !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
}
.theme--dark .clear-all {
  background: rgba(255,212,81,.08) !important;
  border-color: rgba(255,212,81,.15) !important;
  color: #ffd451 !important;
}

/* Field labels */
.theme--dark .field-label {
  color: #8b8d9e;
}

/* Caption text in footer */
.theme--dark .caption.grey--text {
  color: #8b8d9e !important;
}

/* Focus for dark — border only, no ring */
.theme--dark ::v-deep .v-input--is-focused .v-input__slot {
  box-shadow: none !important;
}

/* Badge dot */
.theme--dark .badge-dot {
  background: #ffd451;
}

/* Excel export button dark */
.theme--dark .modern-export {
  background: #1b5e20 !important;
}
.theme--dark .modern-export:hover {
  background: #145218 !important;
}

/* Table text dark */
.theme--dark ::v-deep .group-table-bulk td {
  color: #e0e1e6 !important;
}
.theme--dark ::v-deep .group-table-bulk th {
  color: #9ca3af !important;
}
.theme--dark ::v-deep .group-table-bulk .text-no-wrap {
  color: #e0e1e6 !important;
}
.theme--dark .paid-text {
  color: #66bb6a !important;
}
.theme--dark .row-num {
  color: #666 !important;
}
.theme--dark .pct-text-gold {
  color: #ffd451 !important;
}
.theme--dark .s-pct-text-gold {
  color: #ffd451 !important;
}
.theme--dark ::v-deep .green--text.text--darken-2 {
  color: #66bb6a !important;
}
.theme--dark ::v-deep .orange--text.text--darken-2 {
  color: #ffa726 !important;
}
.theme--dark ::v-deep .red--text.text--darken-1 {
  color: #ef5350 !important;
}

/* Badge count dark — dark text on the gold (primary) chip, not white */
.theme--dark .badge-count {
  color: #111318 !important;
}
/* Filter-count badge (gold primary in dark) → dark text, not white */
.theme--dark ::v-deep .v-badge__badge {
  color: #111318 !important;
}

/* Percent bar dark */
.theme--dark .pct-bar {
  background: rgba(255,255,255,.1);
}

/* Row action buttons */
.theme--dark .tc-act {
  border-color: #2a2c3a;
  color: #9ca3af;
  background: none;
}
.theme--dark .tc-act:hover {
  border-color: #ffd451;
  color: #ffd451;
  background: rgba(255,212,81,.06);
}
.theme--dark .tc-act--delete:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239,68,68,.06);
}

/* Group modal dark */
.theme--dark .grp-modal {
  background: #1a1b23;
  box-shadow: 0 24px 64px rgba(0,0,0,.4);
}
.theme--dark .grp-modal-header h3 {
  color: #e4e5eb;
}
.theme--dark .grp-modal-close {
  border-color: #2a2c3a;
  color: #8b8d9e;
}
.theme--dark .grp-modal-close:hover {
  border-color: #3a3c4a;
  background: #22232e;
  color: #e4e5eb;
}
.theme--dark .grp-label {
  color: #8b8d9e;
}
.theme--dark .grp-btn-cancel {
  border-color: #2a2c3a;
  color: #8b8d9e;
}
.theme--dark .grp-btn-cancel:hover {
  border-color: #3a3c4a;
  color: #e4e5eb;
}
.theme--dark .grp-btn-save {
  background: #ffd451;
  color: #111318;
}
.theme--dark .grp-btn-save:hover {
  background: #ffdc6e;
}

/* ===== TOP 5 ROWS (by payment %) ===== */
::v-deep .gold-row {
  background: linear-gradient(90deg, rgba(46,125,50,.08), rgba(76,175,80,.03)) !important;
  border-left: 3px solid #43a047;
}
::v-deep .gold-row:hover {
  background: linear-gradient(90deg, rgba(46,125,50,.14), rgba(76,175,80,.06)) !important;
}

/* Danger row (000OOO — left students) */
::v-deep .danger-row {
  background: rgba(211,47,47,.1) !important;
  border-left: 3px solid #d32f2f;
}
::v-deep .danger-row:hover {
  background: rgba(211,47,47,.16) !important;
}

.pct-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffd451, #f5c542);
  color: #1a1500;
  font-weight: 800;
  font-size: 10px;
  flex-shrink: 0;
}
.pct-bar-gold {
  background: #2e7d32 !important;
}
.pct-text-gold {
  color: #2e7d32 !important;
  font-weight: 800 !important;
}

/* Top 5 rows dark theme */
.theme--dark ::v-deep .gold-row {
  background: linear-gradient(90deg, rgba(46,125,50,.08), rgba(76,175,80,.03)) !important;
  border-left: 3px solid rgba(67,160,71,.6);
}
.theme--dark ::v-deep .gold-row:hover {
  background: linear-gradient(90deg, rgba(46,125,50,.14), rgba(76,175,80,.06)) !important;
}
.theme--dark ::v-deep .danger-row {
  background: rgba(211,47,47,.12) !important;
  border-left: 3px solid rgba(211,47,47,.7);
}
.theme--dark ::v-deep .danger-row:hover {
  background: rgba(211,47,47,.2) !important;
}
</style>

<style>
.grp-dialog-wrap {
  overflow: visible !important;
}
.v-dialog__content .grp-dialog-wrap {
  overflow: visible !important;
}
.grp-dialog-wrap .grp-modal {
  overflow: visible !important;
}
.grp-dialog-wrap .grp-modal-body {
  overflow: visible !important;
}

/* ═══════════════════════════════════════
   PREMIUM DROPDOWN OVERRIDES
   ═══════════════════════════════════════ */
.v-menu__content {
  border-radius: 14px !important;
  border: 1px solid rgba(0,0,0,.08) !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06) !important;
  /* Keep vertical scrolling on the dropdown: `overflow: hidden` here killed
     the scrollbar for every autocomplete/select menu (this style block is
     unscoped and leaks app-wide). Let the menu content own the scroll. */
  overflow-y: auto !important;
  overflow-x: hidden !important;
  animation: grpDropIn .15s ease;
}
@keyframes grpDropIn {
  from { opacity: 0; transform: translateY(-6px) scale(.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.v-menu__content .v-list {
  padding: 6px !important;
  background: #fff !important;
}
.v-menu__content .v-list-item {
  border-radius: 8px !important;
  margin: 1px 0 !important;
  min-height: 38px !important;
  padding: 0 12px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #333 !important;
  transition: background .12s ease !important;
}
.v-menu__content .v-list-item:hover,
.v-menu__content .v-list-item:focus {
  background: rgba(0,0,0,.04) !important;
}
.v-menu__content .v-list-item--active {
  background: rgba(25,118,210,.08) !important;
  color: #1565c0 !important;
  font-weight: 600 !important;
}
.v-menu__content .v-list-item--active::before {
  opacity: 0 !important;
}
.v-menu__content .v-list-item__action {
  margin-right: 10px !important;
}
.v-menu__content .v-list::-webkit-scrollbar { width: 5px; }
.v-menu__content .v-list::-webkit-scrollbar-track { background: transparent; }
.v-menu__content .v-list::-webkit-scrollbar-thumb { background: rgba(0,0,0,.12); border-radius: 3px; }

/* Input slots — clean modern look */
.group-page .v-input--outlined .v-input__slot {
  border-radius: 10px !important;
  background: #fff !important;
  transition: border-color .15s ease !important;
}
.group-page .v-input--outlined fieldset {
  border: 1px solid #e0e0e0 !important;
}
.group-page .v-input--outlined.v-input--is-focused fieldset {
  border-color: #00274c !important;
  border-width: 1.5px !important;
}
.group-page .v-input--outlined.v-input--is-focused .v-input__slot {
  background: #fff !important;
}
/* Hide floating label */
.group-page .v-input--outlined legend { display: none !important; }
.group-page .v-label--active { display: none !important; }
/* Chips inside multi-select */
.group-page .v-select__selections .v-chip {
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  height: 24px !important;
  background: rgba(25,118,210,.06) !important;
  color: #1565c0 !important;
  border: none !important;
}
.group-page .v-select__selections .v-chip .v-chip__close {
  color: #1565c0 !important;
}
/* Arrow icon */
.group-page .v-input__append-inner .v-icon {
  font-size: 20px !important;
  color: #bdbdbd !important;
}
.group-page .v-input--is-focused .v-input__append-inner .v-icon {
  color: #00274c !important;
}
/* Clear icon */
.group-page .v-input__icon--clear .v-icon {
  font-size: 18px !important;
  color: #bdbdbd !important;
}
.group-page .v-input__icon--clear .v-icon:hover {
  color: #757575 !important;
}

/* ===== DARK DROPDOWN ===== */
.theme--dark .v-menu__content {
  border-color: #2a2c3a !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.4), 0 2px 8px rgba(0,0,0,.2) !important;
}
.theme--dark .v-menu__content .v-list {
  background: #1a1b23 !important;
}
.theme--dark .v-menu__content .v-list-item {
  color: #d5d6de !important;
}
.theme--dark .v-menu__content .v-list-item:hover {
  background: rgba(255,255,255,.05) !important;
}
.theme--dark .v-menu__content .v-list-item--active {
  background: rgba(255,212,81,.08) !important;
  color: #ffd451 !important;
}
.theme--dark .group-page .v-input--outlined .v-input__slot {
  background: #1e1f2a !important;
}
.theme--dark .group-page .v-input--outlined fieldset {
  border-color: #333546 !important;
}
.theme--dark .group-page .v-input--outlined.v-input--is-focused fieldset {
  border-color: #ffd451 !important;
}
.theme--dark .group-page .v-input--outlined.v-input--is-focused .v-input__slot {
  background: #1a1b23 !important;
}
.theme--dark .group-page .v-input__append-inner .v-icon {
  color: #555 !important;
}
.theme--dark .group-page .v-input--is-focused .v-input__append-inner .v-icon {
  color: #ffd451 !important;
}
.theme--dark .group-page .v-input__icon--clear .v-icon {
  color: #555 !important;
}
.theme--dark .group-page .v-select__selections .v-chip {
  background: rgba(255,212,81,.1) !important;
  color: #ffd451 !important;
}
.theme--dark .v-menu__content .v-list::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,.12);
}
</style>
